import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DiscountCouponService from "./Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Col, CardBody, Card } from "reactstrap";
import PermissionAccess from '../../PermissionAccess/Access.js';

const AddDocumentSettingsModal = ({ showModalAddDiscountCoupon, handleToggleModalAddDiscountCoupon, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("");
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();

  const [data, setData] = useState({
    title: '',
    icon: '',
    type: '',
    specializations: [],
  });

  const [errors, setErrors] = useState({
    title: '',
    icon: '',
    type: '',
    specializations: '',
  });

  const [specializationOptions, setSpecializationOptions] = useState([]);

  useEffect(() => {
    const fetchSpecializations = async () => {
      setLoading(true);
      DiscountCouponService.getAllSpecializations(authData.sessionID, 0, 9999999, '', '', '').then((response) => {
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          if (response.data.status === true) {
            const options = response.data.data.map(spec => ({
              value: spec._id,
              label: spec.name
            }));
            // Add Select All option at the top
            setSpecializationOptions([{ value: '__all__', label: t('Select All') }, ...options]);
          } else {
            Alerts.swalErrorAlert(t("Something went wrong, Please try again."));
          }
        }
      }, (error) => {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      });
    };
    fetchSpecializations();
  }, [authData.sessionID, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target || e;
    let error = '';

    if (name === 'title' && !value) {
      error = 'Title is required';
    } else if (name === 'icon') {
      if (!value) error = 'Icon is required';
    } else if (name === 'type') {
      if (!value) error = 'Type is required';
      setType(value);
    }

    setData(prevData => ({ ...prevData, [name]: value }));
    setErrors(prevErrors => ({ ...prevErrors, [name]: error }));
  };

  const handleSpecializationChange = (selectedOptions) => {
    if (!selectedOptions) {
      setData(prevData => ({ ...prevData, specializations: [] }));
      setErrors(prevErrors => ({ ...prevErrors, specializations: 'At least one specialization is required' }));
      return;
    }
    // Check if Select All was clicked
    const selectAllOption = selectedOptions.find(option => option.value === '__all__');
    const allOptions = specializationOptions.filter(option => option.value !== '__all__');
    if (selectAllOption) {
      // If Select All was clicked, select all options (but don't include Select All itself)
      setData(prevData => ({ ...prevData, specializations: allOptions.map(option => option.value) }));
      setErrors(prevErrors => ({ ...prevErrors, specializations: '' }));
    } else {
      // Regular selection - filter out Select All from the selected values
      const selectedValues = selectedOptions
        .filter(option => option.value !== '__all__')
        .map(option => option.value);
      setData(prevData => ({ ...prevData, specializations: selectedValues }));
      setErrors(prevErrors => ({ ...prevErrors, specializations: selectedValues.length > 0 ? '' : 'At least one specialization is required' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let validationErrors = {};

    if (!data.title) { validationErrors.title = 'Title is required'; }
    if (!data.icon) { validationErrors.icon = 'Icon is required'; }
    if (!data.type) { validationErrors.type = 'Type is required'; }
    if (data.specializations.length === 0) { validationErrors.specializations = 'At least one specialization is required'; }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      DiscountCouponService.AddDocument(authData.sessionID, data).then((response) => {
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          if (response.data.status === true) {
            resetForm();
            handleToggleModalAddDiscountCoupon();
            onSuccess();
            Alerts.swalSuccessAlert(t("Document added successfully."));
          } else {
            validationErrors.title = response.data.message || 'An error occurred';
            setErrors(validationErrors);
          }
        }
      }, (error) => {
        setLoading(false);
        const errContent = (error.response && error.response.data) || error.message || error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      });
    }
  };

  const resetForm = () => {
    setData({
      title: '',
      icon: '',
      type: '',
      specializations: [],
    });
  };

  const handleClose = () => {
    setErrors({
      title: '',
      icon: '',
      type: '',
      specializations: '',
    });
    handleToggleModalAddDiscountCoupon();
  };

  return (
    <div className={showModalAddDiscountCoupon ? "modal d-block mt-4" : "modal"} style={{ display: showModalAddDiscountCoupon ? 'block' : 'none', backgroundColor: 'rgba(0, 0, 0, 0.5)' }} tabIndex="-1" role="dialog">
      <form onSubmit={handleSubmit}>
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">{t("Add Document")}</h5>
            </div>
            <div className="modal-body">
              <Col md="12">
                <Card className="main-card mb-3">
                  <CardBody>
                    <LoadingOverlay
                      tag="div"
                      active={loading}
                      styles={{ overlay: (base) => ({ ...base }) }}
                      spinner={<DNALoader />}
                    >
                      <input type="hidden" className="form-control" name="business_id" />

                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="title" className="col-form-label">{t("Title")}:</label>
                            <input type="text" className="form-control" placeholder="Enter Title"
                              name="title"
                              onChange={handleChange}
                              value={data.title}
                            />
                            {errors.title && <strong className="error text-danger">{errors.title}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="icon" className="col-form-label">{t("Icon")}:</label>
                            <input type="text" className="form-control" placeholder="Enter Icon"
                              name="icon"
                              onChange={handleChange}
                              value={data.icon}
                            />
                            {errors.icon && <strong className="error text-danger">{errors.icon}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="type-select" className="col-form-label">{t("Type")}:</label>
                            <select className="form-control"
                              name="type"
                              onChange={handleChange}
                              value={data.type}
                            >
                              <option value="">{t("Select Type")}</option>
                              <option value="Doctor">{t("Doctor")}</option>
                              <option value="Nurse">{t("Nurse")}</option>
                              <option value="Both">{t("Both")}</option>
                            </select>
                            {errors.type && <strong className="error text-danger">{errors.type}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label htmlFor="specializations" className="col-form-label">{t("Specializations")}:</label>
                            <Select
                              isMulti
                              name="specializations"
                              options={specializationOptions}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              value={
                                // If all options (except Select All) are selected, show Select All as selected too
                                data.specializations.length === specializationOptions.filter(option => option.value !== '__all__').length && specializationOptions.length > 1
                                  ? [specializationOptions[0], ...specializationOptions.filter(option => data.specializations.includes(option.value) && option.value !== '__all__')]
                                  : specializationOptions.filter(option => data.specializations.includes(option.value) && option.value !== '__all__')
                              }
                              onChange={handleSpecializationChange}
                            />
                            {errors.specializations && <strong className="error text-danger">{errors.specializations}</strong>}
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="text-center mt-4">
                          <button type="button" className="btn btn-outline-light btn-hover-shine me-3" onClick={handleClose}>{t("Close")}</button>
                          <button type="submit" className="btn btn-custom-color">{t("Save")}</button>
                        </div>
                      </div>
                    </LoadingOverlay>
                  </CardBody>
                </Card>
              </Col>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

AddDocumentSettingsModal.propTypes = {
  showModalAddDiscountCoupon: PropTypes.bool.isRequired,
  handleToggleModalAddDiscountCoupon: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default AddDocumentSettingsModal;
